// ===========================================
// Permission-filtered admin navigation (staff view)
// ===========================================
// Pure so the owner-only items can be pinned without rendering the layout.
// A staff login (isStaff, not ADMIN) sees only what its role grants; Roles &
// Permissions and the System section's owner-only items follow `isOwner`,
// never a permission (roles plan R1/R9). `isSuperAdmin` is NOT read: "Full
// access" is retired, so a stale flag grants nothing.

interface NavItem {
  label: string;
}
interface NavSection<I extends NavItem> {
  id: string;
  items: I[];
}
export interface NavConfig<I extends NavItem, S extends NavSection<I>> {
  standalone: I[];
  sections: S[];
}

export interface NavFilterContext {
  isStaff: boolean;
  role?: string;
  canAny: (resource: string) => boolean;
  isOwner: boolean;
}

// Maps nav item labels / section IDs to permission resource strings used by usePermissions
export const NAV_PERMISSION_MAP: Record<string, string> = {
  // Standalone items (keyed by label)
  'Dashboard': 'dashboard',
  'Messages': 'messages',
  'Calendar': 'scheduling',
  'Meet & Greet': 'meet_and_greet',
  'Enquiries': 'enquiries',
  // Events section (keyed by section id); children inherit via section-level gate.
  'events': 'events',
  // Sections (keyed by section id)
  'school-setup': 'school_setup',
  'class-config': 'class_config',
  'gamification': 'gamification',
  'people': 'people_teachers',
  'scheduling': 'scheduling',
  'operations': 'operations_invoices',
  'reports': 'reports',
  'content': 'communications_newsletters',
  'system': 'system_settings',
};

const ITEM_PERMISSION_MAP: Record<string, string> = {
  Teachers: 'people_teachers',
  Families: 'people_families',
  Students: 'people_students',
  // Enquiries Phase 6: the onboarding check-in page.
  'Onboarding check-in': 'student_onboarding',
  Invoices: 'operations_invoices',
  Subscriptions: 'operations_subscriptions',
  'Class Pricing': 'operations_pricing',
  Products: 'operations_pricing',
  'Payment Settings': 'operations_payments',
  Newsletters: 'communications_newsletters',
  'Custom Emails': 'communications_emails',
  // EXACT STRING MATCH against the nav item's `label` - a typo here does not
  // fail loudly, it just leaves the item ungated.
  'Email Sequences': 'communications_emails',
  'System Emails': 'communications_settings',
  // The backend gate is communications_emails.read; without this entry the link would show to a
  // newsletters- or settings-only role and open the lock page.
  'Email Log': 'communications_emails',
  // #374 Phase 2: the report is gated by registration_fees.read, not by `reports`.
  'Registration fees not applied': 'registration_fees',
  'Registration fees paid': 'registration_fees',
  'Registration fees kept': 'registration_fees',
  'Email Settings': 'communications_settings',
  'SMS & WhatsApp Campaigns': 'communications_emails',
  'System SMS/WhatsApp': 'communications_settings',
  Integrations: 'integrations',
  'Import / Export': 'import_export',
  'Security Audit': 'security_audit',
  'System Settings': 'system_settings',
};

/** Nav items only the owner tier sees, whatever a staff role holds. EXACT label match. */
export const OWNER_ONLY_ITEMS: readonly string[] = ['Roles & Permissions', 'Retro Edits', 'Family Credit'];

export function filterAdminNavigation<I extends NavItem, S extends NavSection<I>>(
  base: NavConfig<I, S>,
  { isStaff, role, canAny, isOwner }: NavFilterContext
): NavConfig<I, S> {
  // ADMINs (and non-staff) always receive the complete config.
  if (!isStaff || role === 'ADMIN') return base;

  const standalone = base.standalone.filter((item) => {
    const resource = NAV_PERMISSION_MAP[item.label];
    return resource ? canAny(resource) : false;
  });

  const sections = base.sections
    .filter((section) => {
      // Multi-resource sections: pass if the user has ANY relevant permission
      if (section.id === 'people') {
        return (
          canAny('people_teachers') ||
          canAny('people_families') ||
          canAny('people_students') ||
          canAny('student_onboarding')
        );
      }
      if (section.id === 'operations') {
        return (
          canAny('operations_invoices') ||
          canAny('operations_subscriptions') ||
          canAny('operations_pricing') ||
          canAny('operations_payments')
        );
      }
      if (section.id === 'content') {
        return (
          canAny('communications_newsletters') ||
          canAny('communications_emails') ||
          canAny('communications_settings')
        );
      }
      if (section.id === 'reports') {
        // A role with only registration_fees.read still sees its one report here.
        return canAny('reports') || canAny('registration_fees');
      }
      if (section.id === 'system') {
        return (
          canAny('system_settings') ||
          canAny('integrations') ||
          canAny('import_export') ||
          canAny('security_audit') ||
          isOwner
        );
      }
      const resource = NAV_PERMISSION_MAP[section.id];
      return resource ? canAny(resource) : false;
    })
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // Roles & Staff is owner-only (roles plan R1/R9), never a staff permission; so is the
        // Retro Edits report, which audits the staff (roles slice 2, V25/V27).
        if (OWNER_ONLY_ITEMS.includes(item.label)) return isOwner;
        const resource = ITEM_PERMISSION_MAP[item.label];
        // No specific mapping: fall through to the section-level permission.
        if (!resource) return section.id === 'reports' ? canAny('reports') : true;
        return canAny(resource);
      }),
    }))
    // Drop sections that became empty after item filtering
    .filter((section) => section.items.length > 0);

  return { standalone, sections };
}
